import {measurementIdentity} from "./measurement-interval";
import {AfterViewInit, Component, HostListener, Input, OnInit, TemplateRef, Type, ViewChild, ViewContainerRef} from '@angular/core';
import {View} from "src/app/common/view";
import {Handler} from "src/app/common/handler";
import {HandleQuery} from "../../common/handle-query";
import {ChartOptions, ChartOptionType} from "../dashboard/dashboard.types";
import {
    catchError,
    combineLatest,
    map,
    mergeMap,
    Observable,
    of,
    pairwise,
    ReplaySubject,
    shareReplay,
    skipWhile,
    Subject,
    switchMap,
    take
} from "rxjs";
import lodash, {cloneDeep} from "lodash";
import {DashboardContext} from "../dashboard/dashboard.context";
import {AppContext, CompletenessInfo, IncidentsResult} from "../../app-context";
import {
    AggregatedDataPoint,
    Connection,
    ContractMeasurementResult,
    DataType,
    DayOfWeekMeasurementsResult,
    GetContractMeasurements,
    GetDayOfWeekMeasurements,
    GetMeasurements,
    GetPortfolioMeasurementsNew,
    Location,
    MeasurementsResult,
    MeterType,
    Organisation,
    PortfolioMeasurementsResult,
    PortfolioMeasurementsResultNew,
    SourceIds,
    TimeRange,
    TimeResolution
} from "@flowmaps-com/flowmaps-typescriptmodels";
import rfdc from 'rfdc';
import {openModal, sendQuery} from "../../common/app-common-utils";
import moment from "moment";
import {
    asSourceIds,
    entityToSourceInfo,
    SourceInfo,
    SourceSelectionChangeEvent
} from "../../utils/source-providers/sources-provider";
import {EntityType} from "../../handlers/entity";
import {HandleEvent} from "../../common/handle-event";
import {ChartDataPerMeasurement, DashboardTime, TimeChangedEvent} from "../../utils/chart-data-provider";
import {ChartModalOptions} from "../charts/chart-utils.service";
import {HandleCommand} from "../../common/handle-command";
import {MeterViewEntity} from "../../handlers/meter-views-standalone-handler";
import {filter, tap} from 'rxjs/operators';
import {resolveMeasurementsTimeRange} from './measurements-time-range';
import {
    canReuseDashboardMeasurements,
    dashboardDataMatchesFilters,
    dashboardQueryFingerprint,
    withMeasurementRefresh
} from './refreshable-measurements';
import {resolveDashboardTypeState} from './dashboard-type-state';
import {resolveMeterDashboardSources} from './meter-dashboard-sources';
import {
    createCrremMeasurementStream,
    CrremMeasurementChannel,
    CrremMeasurementsResult
} from '../../utils/crrem/crrem-measurements';
import {
    applyElectricityConnectionContext,
    electricityConnectionContextSources,
    electricityMeterSelection
} from './electricity-connection-context';

const clone = rfdc();

@Component({
    selector: 'app-measurements-handler',
    templateUrl: './measurements-handler.component.html',
    styleUrls: ['./measurements-handler.component.scss']
})
@Handler()
export class MeasurementsHandlerComponent extends View implements OnInit, AfterViewInit {
    data: MeasurementsHandlerComponentData;
    @Input() locationDashboard: boolean;
    @Input() useWrapperForChartOptions: boolean;
    @ViewChild('container', {read: ViewContainerRef}) container: ViewContainerRef;
    dashboardHistory: DashboardTime[] = [];

    filtersSubject: Subject<DataQueryFilters>;
    isPortfolioSubject: Subject<boolean>;
    isMeterDashboardSubject: Subject<boolean>;
    chartOptionsSubject: Subject<{ [P in ChartOptionType]?: ChartOptions }>;
    lastOptionsValue: { [P in ChartOptionType]?: ChartOptions };
    rendered: boolean;
    rendering: boolean;
    meterDashboard: boolean;
    dashboardType: string;
    private readonly measurementsDataStreams = new Map<string, Observable<ChartDataPerMeasurement>>();
    private readonly totalsStreams = new Map<string, Observable<ChartDataPerMeasurement>>();
    private readonly completenessStreams = new Map<string, Observable<CompletenessInfo[]>>();
    private readonly measurementsRefreshSubject = new Subject<void>();
    private contractMeasurementsStream?: Observable<{result: ContractMeasurementResult; fingerprint: string}>;
    private readonly energyCompassMeasurementStreams = new Map<string, Observable<ChartDataPerMeasurement>>();
    private dashboardMeasurementsStream?: Observable<ChartDataPerMeasurement>;

    constructor() {
        super();
        this.filtersSubject = new ReplaySubject(1);
        this.chartOptionsSubject = new ReplaySubject(1);
        this.isMeterDashboardSubject = new ReplaySubject(1);
        this.publishChartOptionsChange();
        this.isPortfolioSubject = new ReplaySubject(1);
        this.isPortfolioSubject.pipe(pairwise())
            .subscribe(([previousValue, currentValue]) => {
                if (previousValue !== currentValue) {
                    this.publishEvent("dashboardTypeChanged", currentValue);
                }
            });
        this.isMeterDashboardSubject
            .subscribe((currentValue) => {
                this.meterDashboard = currentValue;
                this.publishEvent("meterDashboardChanged", currentValue);
            });
        this.isMeterDashboardSubject.next(false);
    }

    ngOnInit() {
        if (lodash.has(this.data, "allSelectedSources")) {
            this.updateIsLocationDashboard();
        }
        this.isPortfolioSubject.next(!this.locationDashboard);
    }

    ngAfterViewInit() {
        this.rendering = true;
        this.setData = this.data;
        this.rendered = true;
    }

    private updateIsLocationDashboard(filtersChanged = false) {
        const isLocationDashboard = !!this.dashboardType && this.dashboardType !== "portfolio" && this.dashboardType !== "meter" && !filtersChanged;
        this.locationDashboard = (this.data.selectedSources?.length === 1 && this.data.selectedSources[0]?.type === EntityType.location) || isLocationDashboard
    }

    @Input("data")
    set setData(data: MeasurementsHandlerComponentData) {
        const backupFilters: DataQueryFilters = {
            timeRange: this.data?.timeRange,
            allSelectedSources: this.data?.allSelectedSources,
            sources: this.data?.selectedSources,
            cleanedSources: this.cleanupSelection(this.data?.selectedSources),
            fullScreen: data?.chartModalData?.fullScreen
        };
        this.data = data;
        if (this.rendering) {
            if (data.component) {
                this.renderSubComponent(data.component);
            }
        }
        this.publishFiltersChanged({
            timeRange: this.dashboardTimeToQuery(this.data.timeRange),
            sources: this.data.selectedSources,
            cleanedSources: this.cleanupSelection(this.data.selectedSources),
            allSelectedSources: this.data.allSelectedSources,
            fullScreen: this.data.chartModalData?.fullScreen
        }, backupFilters);
        this.publishChartOptionsChange();
    }

    private publishChartOptionsChange() {
        const newValue = this.data?.chartOptions || DashboardContext.defaultDashboard().info.chartOptions;
        if (newValue !== this.lastOptionsValue) {
            this.lastOptionsValue = newValue;
            this.chartOptionsSubject.next(newValue);
        }
    }

    private publishFiltersChanged(filters: DataQueryFilters, currentFilters?: DataQueryFilters) {
        const sourcesChanged = !lodash.isEqual(lodash.omit(filters, "allSelectedSources"), lodash.omit(currentFilters, "allSelectedSources"));
        if (sourcesChanged) {
            if (!lodash.isEqual(filters.sources, currentFilters?.sources)) {
                // A backend-resolved mode belongs to its selection. Resolve the new
                // selection without narrowing a selected parent to its meters.
                this.dashboardType = undefined;
                if (this.meterDashboard) this.updateMeterDashboard(false);
            }
            this.updateIsLocationDashboard(true);
            this.isPortfolioSubject.next(!this.locationDashboard);
            this.publishEvent("filtersChanged", filters);
        }
        if (sourcesChanged || !this.rendered || this.data.chartModalData?.fullScreen) {
            this.filtersSubject.next(filters);
        }
    }

    @HandleEvent("sourceSelectionChange")
    sourceSelectionChange(selectedSources: SourceSelectionChangeEvent, event: CustomEvent) {
        this.data.selectedSources = selectedSources.selectedItems;
        this.data.allSelectedSources = selectedSources.allSelectedItems;
        this.filtersSubject.pipe(take(1)).subscribe(f => this.publishFiltersChanged((<DataQueryFilters>{
            ...f,
            sources: this.data.selectedSources,
            cleanedSources: this.cleanupSelection(this.data.selectedSources),
            allSelectedSources: this.data.allSelectedSources
        }), f));
        if (this.data.chartModalData?.fullScreen) {
            event.stopPropagation();
            return false;
        }
    }

    @HandleEvent("timeChanged")
    timeChanged(payload: TimeChangedEvent, event: CustomEvent) {
        if (payload.historyType === "add") {
            this.dashboardHistory.push(this.data.timeRange);
        }
        if (payload.historyType === "remove") {
            this.dashboardHistory.pop();
        }
        this.data.timeRange = payload.timeRange;
        this.filtersSubject.pipe(take(1)).subscribe(f => this.publishFiltersChanged((<DataQueryFilters>{
            ...f,
            timeRange: this.dashboardTimeToQuery(this.data.timeRange)
        }), f));
        if (this.data.chartModalData?.fullScreen) {
            event.stopPropagation();
            return false;
        }
    }

    @HandleQuery("getDashboardHistory")
    getDashboardHistory(): Observable<DashboardTime[]> {
        return of(this.dashboardHistory);
    }

    @HandleQuery("isPortfolio")
    isPortfolio(): Observable<boolean> {
        return this.isPortfolioSubject;
    }

    @HandleQuery("isMeterDashboard")
    isMeterDashboard(): Subject<boolean> {
        return this.isMeterDashboardSubject;
    }

    @HandleQuery("groupByEntity")
    groupByEntity(): Observable<boolean> {
        return this.subscribeTo("getChartDataQueryFilters")
            .pipe(skipWhile((d: DataQueryFilters) => !d.sources))
            .pipe(map((d: DataQueryFilters) => this.locationDashboard || this.meterDashboard || (!!this.data.selectedSources.length
                    && this.data.selectedSources.every(s => s.type === EntityType.meter)) ||
                    this.data.selectedSources.length == 1 && this.data.selectedSources[0].type === EntityType.connection &&
                    this.data.selectedSources[0].source.connection.meters.some(m => m.info.type === MeterType.INTERMEDIATE)));
    }

    @HandleQuery("onlyMetersSelected")
    onlyMetersSelected(): Observable<boolean> {
        return this.subscribeTo("getChartDataQueryFilters")
            .pipe(skipWhile((d: DataQueryFilters) => !d.sources))
            .pipe(map((d: DataQueryFilters) => (!!this.data.selectedSources.length
                && this.data.selectedSources.every(s => s.type === EntityType.meter))
                || this.meterDashboard
            ));
    }

    @HandleQuery("getChartOptions")
    getChartOptions(type: ChartOptionType): Observable<ChartOptions> {
        if (this.useWrapperForChartOptions) {
            return this.sendQuery("getWrappedChartOptions", type);
        }
        if (lodash.isEmpty(type)) {
            console.warn("Dashboard chart option type is required to get chart options");
            return of(null);
        }
        return this.chartOptionsSubject
            .pipe(map(c => c[type] || DashboardContext.defaultDashboard().info.chartOptions[type]));
    }

    @HandleQuery("getChartDataQueryFilters")
    getChartDataQueryFilters(): Subject<DataQueryFilters> {
        return this.filtersSubject;
    }

    @HandleQuery("getTimeRange")
    getTimeRange(): Observable<DashboardTime> {
        return this.subscribeTo("getChartDataQueryFilters").pipe(map((f: DataQueryFilters) => f.timeRange));
    }

    @HandleQuery("getContractMeasurements")
    getContractMeasurements(): Observable<ContractMeasurementResult> {
        if (!this.contractMeasurementsStream) {
            this.contractMeasurementsStream = withMeasurementRefresh(
                this.getChartDataQueryFilters().pipe(
                    filter((filters: DataQueryFilters) => !!filters.timeRange && !!filters.sources)),
                this.measurementsRefreshSubject
            ).pipe(
                switchMap((filters: DataQueryFilters) => {
                    const sources = this.measurementQuerySources(filters);
                    const fingerprint = dashboardQueryFingerprint(filters.timeRange, sources);
                    return sendQuery("com.flowmaps.api.measurements.GetContractMeasurements", <GetContractMeasurements>{
                        timeRange: filters.timeRange,
                        sourceIds: asSourceIds(sources),
                        resolution: filters.timeRange.resolution,
                        unrounded: ![TimeResolution.year, TimeResolution.month].includes(filters.timeRange.resolution)
                    }, {caching: false, showSpinner: false}).pipe(map(result => ({result, fingerprint})));
                }),
                shareReplay({bufferSize: 1, refCount: true})
            );
        }
        return combineLatest([this.contractMeasurementsStream, this.getChartDataQueryFilters()]).pipe(
            filter(([value, filters]) => value.fingerprint === dashboardQueryFingerprint(
                filters.timeRange, this.measurementQuerySources(filters))),
            map(([value]) => value.result)
        );
    }

    @HandleQuery("getDayOfWeekMeasurements")
    getDayOfWeekMeasurements(): Observable<DayOfWeekMeasurementsResult> {
        return this.subscribeTo("getChartDataQueryFilters").pipe(mergeMap((filters: DataQueryFilters) =>
            filters.timeRange && filters.sources && moment(filters.timeRange.end).diff(moment(filters.timeRange.start), "day") < 367
                ? sendQuery("com.flowmaps.api.measurements.GetDayOfWeekMeasurements", <GetDayOfWeekMeasurements>{
                    timeRange: filters.timeRange,
                    sources: asSourceIds(this.measurementQuerySources(filters))
                }) : of({measurements: {}})));
    }

    private crremStream: Observable<CrremMeasurementsResult>;

    @HandleQuery("getCrremMeasurementChannel")
    getCrremMeasurementChannel(): CrremMeasurementChannel {
        if (!this.crremStream) {
            const currentYear = new Date().getFullYear();
            const timeRange = {start: '2020-01-01T00:00:00', end: `${currentYear}-01-01T00:00:00`};
            const contexts = combineLatest([this.filtersSubject, this.isMeterDashboardSubject]).pipe(
                filter(([filters]) => !!filters?.sources),
                map(([filters, meterDashboard]) => {
                    const selected = this.measurementQuerySources(filters);
                    return {
                        sources: asSourceIds(selected),
                        locationId: selected.length === 1 && selected[0].type === EntityType.location ? selected[0].id : null,
                        locationDashboard: this.locationDashboard, meterDashboard
                    };
                }));
            this.crremStream = createCrremMeasurementStream(contexts, this.measurementsRefreshSubject, sources =>
                combineLatest([
                    sendQuery("com.flowmaps.api.measurements.GetMeasurements", <GetMeasurements>{
                        timeRange, sources, resolution: TimeResolution.month,
                        unrounded: true, addWeather: false, addPower: false
                    }, {caching: false, showSpinner: false}),
                    this.sendQuery("getPrimaryMeterViews", sources).pipe(catchError(() => of([])))
                ]).pipe(map(([measurements, meterViews]) => {
                    const coverage: Record<number, CompletenessInfo[]> = {};
                    for (let year = 2020; year < currentYear; year++) {
                        coverage[year] = AppContext.computeCompletenessPerTimeRange(meterViews,
                            {start: `${year}-01-01T00:00:00`, end: `${year+1}-01-01T00:00:00`}, []);
                    }
                    return {measurements, coverage};
                })));
        }
        return {measurements$: this.crremStream};
    }

    @HandleQuery("getEnergyCompassMeasurements")
    getEnergyCompassMeasurements(payload: EnergyCompassMeasurementsPayload = {}): Observable<ChartDataPerMeasurement> {
        const requestedYear = Number.isInteger(payload?.year) ? payload.year : null;
        const streamKey = requestedYear == null ? 'all' : String(requestedYear);
        const existingStream = this.energyCompassMeasurementStreams.get(streamKey);
        if (existingStream) return this.forCurrentMeasurementSources(existingStream);
        const stream = this.subscribeTo("getChartDataQueryFilters").pipe(
            filter((filters: DataQueryFilters) => !!filters.timeRange && !!filters.sources),
            switchMap((filters: DataQueryFilters) => {
                const timeRange = requestedYear == null ? filters.timeRange : {
                    start: moment({year: requestedYear, month: 0, day: 1}).format('YYYY-MM-DDTHH:mm:ss'),
                    end: moment({year: requestedYear + 1, month: 0, day: 1}).format('YYYY-MM-DDTHH:mm:ss')
                };
                const selectedSources = this.measurementQuerySources(filters);
                const queryFingerprint = dashboardQueryFingerprint(filters.timeRange, selectedSources);
                return sendQuery(
                    "com.flowmaps.api.measurements.GetPortfolioMeasurementsNew",
                    <GetPortfolioMeasurementsNew>{
                        timeRange,
                        sources: asSourceIds(selectedSources),
                        resolution: filters.timeRange.resolution,
                        energyCompassOnly: true,
                        exactEnergyCompassRange: requestedYear != null,
                        // Calculate scores from source precision; round only the displayed result.
                        unrounded: true
                    }).pipe(map(result => <ChartDataPerMeasurement>{
                    totalsPerYear: result.totalsPerYear,
                    byEntity: result.byEntity,
                    entityType: result.entityType,
                    dashboardType: result.dashboardType,
                    timeRange: filters.timeRange,
                    queryFingerprint
                }), tap(result => this.updateDashboardType(result)));
            }),
            shareReplay({bufferSize: 1, refCount: true})
        );
        this.energyCompassMeasurementStreams.set(streamKey, stream);
        return this.forCurrentMeasurementSources(stream);
    }

    @HandleQuery("getDashboardMeasurements")
    getDashboardMeasurements(): Observable<ChartDataPerMeasurement> {
        if (this.dashboardMeasurementsStream) return this.forCurrentFilters(this.dashboardMeasurementsStream);
        this.dashboardMeasurementsStream = withMeasurementRefresh(
            this.subscribeTo("getChartDataQueryFilters").pipe(
                filter((filters: DataQueryFilters) => !!filters?.timeRange && !!filters?.sources)),
            this.measurementsRefreshSubject
        ).pipe(
            switchMap((filters: DataQueryFilters) => this.getPortfolioMeasurementsNew(
                filters, undefined, {includePower: true}
            ).pipe(map(data => this.toChartData(
                data, filters.timeRange, this.currentQueryFingerprint(filters))))),
            tap(data => this.updateDashboardType(data)),
            shareReplay({bufferSize: 1, refCount: true})
        );
        return this.forCurrentFilters(this.dashboardMeasurementsStream);
    }

    @HostListener("window:flowmaps-uploaded-measurements-changed")
    @HandleCommand("refreshDashboardMeasurements")
    refreshDashboardMeasurements(): void {
        this.measurementsRefreshSubject.next();
    }

    @HandleQuery("getCompleteness")
    getCompleteness(timeRange: TimeRange): Observable<CompletenessInfo[]> {
        const streamKey = JSON.stringify(timeRange || null);
        const cachedStream = this.completenessStreams.get(streamKey);
        if (cachedStream) return cachedStream;

        const stream = withMeasurementRefresh(this.getChartDataQueryFilters(), this.measurementsRefreshSubject)
            .pipe(switchMap(result => {
                if (result.sources || this.meterDashboard) {
                    const emptyConnections = (result.allSelectedSources || [])
                        .filter(c => c.type === EntityType.connection)
                        .map(s => s.source.connection as Connection)
                        .filter(s => !s.meters.length);
                    return this.sendQuery(this.meterDashboard ? "getMeterViews" : "getPrimaryMeterViews",
                        asSourceIds(result.sources)).pipe(map((m: MeterViewEntity[]) =>
                        AppContext.computeCompletenessPerTimeRange(m, timeRange, emptyConnections)));
                }
                return of([]);
            }), shareReplay({bufferSize: 1, refCount: true}));
        this.completenessStreams.set(streamKey, stream);
        return stream;
    }

    @HandleQuery("getIncidents")
    getIncidents(): Observable<IncidentsResult[]> {
        return this.getChartDataQueryFilters().pipe(mergeMap(result => {
                if (!result.sources) return of([]);
                return this.sendQuery("getMeterViews", asSourceIds(result.sources)).pipe(
                    map((meterViews: MeterViewEntity[]) =>  AppContext.computeIncidents(meterViews)));
            }));
    }

    @HandleQuery("getMeasurementsData")
    getMeasurementsData(payload: MeasurementsQueryPayload): Observable<ChartDataPerMeasurement> {
        const {
            range, showIncidents = false, includeWeather = false, includePower = false,
            includeClimateNormalization = false, includeElectricityConnectionContext = false
        } = payload;
        const features = {
            includeWeather, includePower, includeClimateNormalization, includeElectricityConnectionContext
        };
        const streamKey = JSON.stringify({range: range || null, showIncidents, ...features});
        const cachedStream = this.measurementsDataStreams.get(streamKey);
        if (cachedStream) return this.forCurrentFilters(cachedStream);

        const base = this.subscribeTo("getChartDataQueryFilters").pipe(
            switchMap((filters: DataQueryFilters) => {
                const timeRange = resolveMeasurementsTimeRange(range, filters.timeRange);
                const selectedData = canReuseDashboardMeasurements(timeRange, filters.timeRange, features)
                    ? this.getDashboardMeasurements().pipe(filter(data =>
                        data.timeRange?.start === timeRange.start && data.timeRange?.end === timeRange.end))
                    : withMeasurementRefresh(of(filters), this.measurementsRefreshSubject).pipe(
                    switchMap(refreshedFilters => this.getPortfolioMeasurementsNew(
                        {...refreshedFilters, timeRange}, undefined, features
                    ).pipe(map(data => this.toChartData(
                        data, timeRange, this.currentQueryFingerprint(refreshedFilters))))));
                if (!includeElectricityConnectionContext) return selectedData;
                const selectedSources = filters.allSelectedSources?.length
                    ? filters.allSelectedSources : filters.sources;
                const meterSelection = electricityMeterSelection(selectedSources);
                const contextSources = electricityConnectionContextSources(selectedSources);
                if (!contextSources) {
                    return selectedData.pipe(map(data => ({
                        ...data,
                        electricityConnectionTotalsAvailable: meterSelection
                            && (meterSelection.unresolved || meterSelection.connectionIds.length > 1)
                            ? false
                            : data.dashboardType ? data.dashboardType !== 'meter' : undefined
                    })));
                }
                return selectedData.pipe(switchMap(data => this.getPortfolioMeasurementsNew(
                    {...filters, timeRange}, contextSources, features
                ).pipe(map(context => applyElectricityConnectionContext(data, context)))));
            }),
            tap(v => this.updateDashboardType(v))
        );
        const incidents = showIncidents ? this.getIncidents() : of([]);
        const stream = combineLatest([base, incidents]).pipe(
            map(([base, incidents]) => ({ ...base, incidents })),
            shareReplay({bufferSize: 1, refCount: true})
        );
        this.measurementsDataStreams.set(streamKey, stream);
        return this.forCurrentFilters(stream);
    }

    private toChartData(data: PortfolioMeasurementsResultNew, timeRange: DashboardTime,
                        queryFingerprint: string): ChartDataPerMeasurement {
        // The response can resolve the initial dashboard mode before its state event is published.
        const responseState = resolveDashboardTypeState(data?.dashboardType);
        const locationDashboard = responseState ? !!responseState.locationId : this.locationDashboard;
        return !lodash.isEmpty(data) ? <ChartDataPerMeasurement>{
            totals: locationDashboard ? data.byEntity : [data.totals],
            byEntity: data.byEntity,
            entityType: data.entityType,
            dashboardType: data.dashboardType,
            timeRange,
            queryFingerprint
        } : {timeRange, queryFingerprint};
    }

    private currentQueryFingerprint(filters: DataQueryFilters): string {
        return dashboardQueryFingerprint(filters?.timeRange,
            filters?.cleanedSources ?? this.cleanupSelection(filters?.sources));
    }

    private forCurrentFilters(stream: Observable<ChartDataPerMeasurement>): Observable<ChartDataPerMeasurement> {
        return combineLatest([stream, this.filtersSubject]).pipe(
            filter(([data, filters]) => dashboardDataMatchesFilters(data, filters?.timeRange,
                filters?.cleanedSources ?? this.cleanupSelection(filters?.sources))),
            map(([data]) => data)
        );
    }

    private forCurrentMeasurementSources(stream: Observable<ChartDataPerMeasurement>): Observable<ChartDataPerMeasurement> {
        return combineLatest([stream, this.filtersSubject]).pipe(
            filter(([data, filters]) => dashboardDataMatchesFilters(data, filters?.timeRange,
                this.measurementQuerySources(filters))),
            map(([data]) => data)
        );
    }

    private updateDashboardType(data: ChartDataPerMeasurement): void {
        const state = resolveDashboardTypeState(data.dashboardType);
        if (!state) return;
        this.dashboardType = data.dashboardType;
        this.updateMeterDashboard(state.meterDashboard);
        this.isPortfolioSubject.next(state.portfolio);
        if (state.locationId) {
            this.locationDashboard = true;
            this.publishEvent("newLocationEntity", state.locationId);
        }
    }

    private updateMeterDashboard(meterDashboard: boolean): void {
        if (this.meterDashboard !== meterDashboard) {
            this.measurementsDataStreams.clear();
            this.totalsStreams.clear();
            this.dashboardMeasurementsStream = undefined;
        }
        this.isMeterDashboardSubject.next(meterDashboard);
    }

    @HandleQuery("getTotals")
    getTotals(payload: MeasurementsQueryPayload): Observable<ChartDataPerMeasurement> {
        const { range, showIncidents = false } = payload;
        const streamKey = JSON.stringify({range: range || null, showIncidents});
        const cachedStream = this.totalsStreams.get(streamKey);
        if (cachedStream) return cachedStream;

        const base = withMeasurementRefresh(
            this.subscribeTo("getChartDataQueryFilters").pipe(filter(filters => !!filters)),
            this.measurementsRefreshSubject
        ).pipe(
            switchMap(filters => {
                const timeRange = resolveMeasurementsTimeRange(range, filters.timeRange);
                return this.getMeasurements({...filters, timeRange}).pipe(map(data => {
                    if (lodash.isEmpty(data)) return {};
                    return {
                        totals: [data],
                        byEntity: [],
                        timeRange
                    } as ChartDataPerMeasurement;
                }));
            })
        );
        const incidents = showIncidents ? this.getIncidents() : of([]);
        const stream = combineLatest([base, incidents]).pipe(
            map(([base, incidents]) => ({ ...base, incidents })),
            shareReplay({bufferSize: 1, refCount: true})
        );
        this.totalsStreams.set(streamKey, stream);
        return stream;
    }

    @HandleQuery("getMeasurements")
    getMeasurements(queryFilters?: DataQueryFilters): Observable<PortfolioMeasurementsResult> {
        return (lodash.isEmpty(queryFilters) ? this.sendQuery("getChartDataQueryFilters") : of(queryFilters)).pipe(mergeMap((filters: DataQueryFilters) =>
            filters.timeRange
                ? sendQuery("com.flowmaps.api.measurements.GetMeasurements", <GetMeasurements>{
                    timeRange: {start: filters.timeRange.start, end: filters.timeRange.end},
                    sources: asSourceIds(this.measurementQuerySources(filters)),
                    resolution: filters.timeRange.resolution,
                    unrounded: ![TimeResolution.year, TimeResolution.month].includes(filters.timeRange.resolution),
                    addWeather: false,
                    addPower: false
                })
                : of({})));
    }


    @HandleQuery("getPortfolioMeasurementsNew")
    getPortfolioMeasurementsNew(queryFilters?: DataQueryFilters, sources?: SourceIds,
                                features: MeasurementsQueryFeatures = {}): Observable<PortfolioMeasurementsResultNew> {
        return (lodash.isEmpty(queryFilters) ? this.sendQuery("getChartDataQueryFilters") : of(queryFilters)).pipe(mergeMap((filters: DataQueryFilters) =>
            filters.timeRange
                ? sendQuery("com.flowmaps.api.measurements.GetPortfolioMeasurementsNew", <GetPortfolioMeasurementsNew>{
                    timeRange: {start: filters.timeRange.start, end: filters.timeRange.end},
                    sources: sources || asSourceIds(this.measurementQuerySources(filters)),
                    resolution: filters.timeRange.resolution,
                    includeWeather: features.includeWeather === true,
                    includePower: features.includePower === true,
                    includeClimateNormalization: features.includeClimateNormalization === true,
                    unrounded: ![TimeResolution.year, TimeResolution.month].includes(filters.timeRange.resolution)
                })
                : of({})));
    }

    private measurementQuerySources(filters?: DataQueryFilters): SourceInfo[] {
        const selectedSources = resolveMeterDashboardSources(
            this.meterDashboard,
            filters?.allSelectedSources || this.data.allSelectedSources,
            filters?.sources || this.data.selectedSources
        );
        return this.meterDashboard ? selectedSources : this.cleanupSelection(selectedSources);
    }

    @HandleCommand("openChartInModal")
    openChartInModal(command: MeasurementsHandlerOpenModalCommand): void {
        const modalOptions = cloneDeep(command.chartModalData || <ChartModalOptions>{});
        modalOptions.fullScreen = true;
        const data = <MeasurementsHandlerComponentData>{
            chartOptions: command.chartOptions,
            component: command.component,
            chartModalData: modalOptions,
            inputs: command.inputs,
            timeRange: this.data.timeRange,
            selectedSources: this.data.selectedSources,
            allSelectedSources: this.data.allSelectedSources,
        };
        // Full-screen charts belong to the application modal layer. Mounting the modal in
        // the originating card makes it inherit row/card height and overflow constraints,
        // which clips the chart and also makes opening progressively slower.
        openModal(MeasurementsHandlerComponent, data, undefined, {
            cssClass: "modal-xl chart-fullscreen-modal",
            style: this.chartModalStyle(command.container, command.maxModalScale, command.modalHeightScale),
            scrollable: false,
            centered: true
        });
    }

    private chartModalStyle(container?: ViewContainerRef, requestedMaxScale = 2,
                            requestedHeightScale = requestedMaxScale): string {
        const source = container?.element?.nativeElement as HTMLElement;
        const bounds = source?.getBoundingClientRect?.();
        const sourceWidth = Math.max(bounds?.width || 0, 320);
        const sourceHeight = Math.max(bounds?.height || 0, 240);
        const maximumWidth = Math.max(Math.min(window.innerWidth - 64, 1600), 320);
        const maximumHeight = Math.max(Math.min(window.innerHeight - 64, window.innerHeight * 0.82), 240);
        const maxScale = Math.max(Math.min(requestedMaxScale, 2), 1);
        const widthScale = Math.min(maxScale, maximumWidth / sourceWidth);
        const heightScale = Math.min(Math.max(Math.min(requestedHeightScale, 2), 1), maximumHeight / sourceHeight);
        const width = Math.round(sourceWidth * widthScale);
        const height = Math.round(sourceHeight * heightScale);
        return `width: ${width}px; min-width: ${width}px; max-width: ${width}px; `
            + `height: ${height}px; min-height: ${height}px; max-height: ${height}px;`;
    }

    private cleanupSelection = (selection: SourceInfo[]): SourceInfo[] => {
        if (!selection?.length) {
            return selection;
        }
        const selectedIds = selection.map(s => s.id);
        let toBeDeleted: string[] = [];
        for (const sourceInfo of selection) {
            const parent = sourceInfo.source.getParentAsEntity();
            if (parent) {
                if (selectedIds.includes(parent.getEntityId())) {
                    toBeDeleted.push(sourceInfo.id);
                }
                if (parent.getChildIds().length > 1 && parent.getChildIds().every(c => selectedIds.includes(c))) {
                    toBeDeleted = toBeDeleted.concat(parent.getChildIds());
                    if (!selection.filter(s => s.id == parent.getEntityId()).length) {
                        selection.push(entityToSourceInfo(parent));
                    }
                }
            }
        }
        if (toBeDeleted.length) {
            const newSelection = lodash.uniqBy(selection.filter(s => !toBeDeleted.includes(s.id)), s => s.id);
            return this.cleanupSelection(newSelection);
        }
        // For meters with children linking to this meter add all the direct subMeters as well
        return selection.flatMap(s => [s].concat(s.type === EntityType.meter ? s.source.getChildEntities().map(s => entityToSourceInfo(s)) : []))
    }

    private renderSubComponent(component: Type<unknown>) {
        const comp = component;
        if (comp instanceof TemplateRef) {
            this.container.createEmbeddedView(comp);
        } else {
            const componentRef = this.container.createComponent(comp);
            const component = componentRef.instance;
            component['data'] = this.data.chartModalData;
            Object.assign(component, this.data.inputs || {});
        }
    }

    private dashboardTimeToQuery = (time: DashboardTime): DashboardTime => {
        if (!time) {
            return null;
        }
        const timeRange = AppContext.timeRangeToQuery(time);
        return {
            start: timeRange.start,
            end: timeRange.end,
            label: time.label,
            resolution: time.resolution
        };
    }

    static mergeAliasesWithLocation(result: PortfolioMeasurementsResult, organisations: Organisation[]): MeasurementsResult[] {
        if (result.entityType !== "location") {
            return result.byEntity;
        }
        const locationMap = new Map<string, Location>();
        organisations.forEach(org => {org.locations.forEach(loc => {
                locationMap.set(loc.locationId, loc);
                loc.aliasIds?.forEach(aliasId => locationMap.set(aliasId, loc));
            });
        });

        const dataMap = new Map<string, MeasurementsResult>();
        result.byEntity?.forEach(r => dataMap.set(r.entityId, clone(r)));

        const mergedResults: MeasurementsResult[] = [];
        dataMap.forEach((r, entityId) => {
            const location = locationMap.get(entityId);
            if (!location || location.locationId !== entityId) {
                return;
            }
            if (!location.aliasIds?.length) {
                mergedResults.push(r);
                return;
            }
            location.aliasIds.forEach(aliasId => {
                const aliasResult = dataMap.get(aliasId);
                if (aliasResult) {
                    r = this.mergeMeasurementsResult(r, aliasResult);
                }
            });
            mergedResults.push(r);
        });
        return mergedResults;
    }

    private static mergeMeasurementsResult(destination: MeasurementsResult, other: MeasurementsResult): MeasurementsResult {
        destination.measurements = MeasurementsHandlerComponent.mergeMeasurements(destination.measurements, other.measurements);
        destination.estimatedMeasurements = MeasurementsHandlerComponent.mergeMeasurements(destination.estimatedMeasurements, other.estimatedMeasurements);
        return destination;
    }

    private static mergeMeasurements(destination: { [P in DataType]?: AggregatedDataPoint[] }, other: { [P in DataType]?: AggregatedDataPoint[] }): { [P in DataType]?: AggregatedDataPoint[] } {
        Object.entries(other).forEach(([dataType, otherPoints]) => {
            if (!otherPoints) return;
            let destPoints = destination[dataType];
            if (!destPoints) {
                destination[dataType] = [...otherPoints];
                return;
            }
            const pointMap = new Map<string, AggregatedDataPoint>();
            destPoints.forEach(p =>
                pointMap.set(measurementIdentity(p), p)
            );
            otherPoints.forEach(p => {
                const key = measurementIdentity(p);
                const existing = pointMap.get(key);
                if (!existing) {
                    destPoints.push(p);
                    pointMap.set(key, p);
                } else {
                    const aggregationMethod = AppContext.getAggregationMethod(dataType as DataType);
                    existing.value = aggregationMethod([existing.value, p.value]);
                }
            });
            destination[dataType] = destPoints;
        });
        return destination;
    }
}

export interface DataQueryFilters {
    timeRange: DashboardTime;
    sources: SourceInfo[];
    cleanedSources: SourceInfo[];
    allSelectedSources: SourceInfo[];
    fullScreen: boolean;
}

export interface MeasurementsHandlerOpenModalCommand {
    chartOptions?: { [P in ChartOptionType]?: ChartOptions };
    component?;
    chartModalData?: ChartModalOptions;
    inputs?: Record<string, unknown>;
    container: ViewContainerRef;
    modalClasses?: string;
    maxModalScale?: number;
    modalHeightScale?: number;
}

export interface MeasurementsHandlerComponentData {
    chartOptions?: { [P in ChartOptionType]?: ChartOptions };
    timeRange?: DashboardTime;
    selectedSources?: SourceInfo[];
    allSelectedSources?: SourceInfo[];
    component?;
    chartModalData?: ChartModalOptions;
    inputs?: Record<string, unknown>;
}

interface MeasurementsQueryPayload {
    range?: DashboardTime;
    showIncidents?: boolean;
    includeWeather?: boolean;
    includePower?: boolean;
    includeClimateNormalization?: boolean;
    includeElectricityConnectionContext?: boolean;
}

interface MeasurementsQueryFeatures {
    includeWeather?: boolean;
    includePower?: boolean;
    includeClimateNormalization?: boolean;
    includeElectricityConnectionContext?: boolean;
}

interface EnergyCompassMeasurementsPayload {
    year?: number;
}
